import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CurrencyDollar, List, SignOut, UserCircle, Password, CaretDown, Bell, WarningCircle } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const pageTitles = {
  '/dashboard': 'Dashboard',
  '/transactions': 'Transactions',
  '/stocks': 'NEPSE Stocks',
  '/banks': 'Accounts',
  '/lendborrow': 'Lend / Borrow',
  '/emi': 'EMI Calculator',
  '/payments': 'Payments',
  '/budgets': 'Budgets',
  '/properties': 'Properties',
  '/reports': 'Reports',
  '/ai-advisor': 'AI Advisor',
  '/alerts': 'Alerts',
  '/my-account': 'My Account',
  '/change-password': 'Change Password',
};

const urgencyClasses = {
  high: 'text-[#A63C24]',
  medium: 'text-[#D98324]',
  low: 'text-[#1E3F2D]',
};

const formatAmount = (value) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(value || 0))}`;

export default function Topbar({ onMenuToggle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    if (!user) return;

    const fetchReminders = () => {
      api.get('/reminders')
        .then((response) => setReminders(response.data || []))
        .catch(() => setReminders([]));
    };

    fetchReminders();
    const interval = setInterval(fetchReminders, 30000);

    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="fixed inset-x-0 top-0 z-40 h-[72px] bg-white border-b border-[#E8E6E1] shadow-[0_1px_0_rgba(28,27,26,0.03)]">
      <div className="h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onMenuToggle}
            className="lg:hidden w-10 h-10 bg-white border border-[#E8E6E1] rounded-xl flex items-center justify-center shadow-sm"
            aria-label="Open menu"
          >
            <List size={20} />
          </button>
          <Link to="/dashboard" className="flex items-center gap-3 min-w-0" data-testid="topbar-logo">
            <div className="w-11 h-11 bg-[#1E3F2D] rounded-2xl flex items-center justify-center shadow-sm">
              <CurrencyDollar size={22} weight="fill" className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-extrabold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Spell FinX</p>
              <p className="text-xs uppercase tracking-[0.18em] text-[#7A766E] truncate">{pageTitles[location.pathname] || 'Workspace'}</p>
            </div>
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="relative flex items-center justify-center w-11 h-11 rounded-2xl border border-[#E8E6E1] text-[#1C1B1A] hover:bg-[#FBF9F6] transition-colors">
                <Bell size={20} weight="fill" />
                {reminders.length > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-[#A63C24] text-white text-[10px] font-bold flex items-center justify-center">
                    {reminders.length}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 rounded-xl border-[#E8E6E1] p-2">
              <DropdownMenuLabel className="flex items-center justify-between text-[#1C1B1A]">
                <span>Upcoming Alerts</span>
                <span className="text-xs font-medium text-[#7A766E]">Next 7 days</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {reminders.length === 0 ? (
                <div className="px-3 py-4 text-sm text-[#7A766E]">No events within 7 days.</div>
              ) : (
                reminders.map((item, index) => (
                  <div key={`${item.type}-${item.date}-${index}`} className="px-3 py-2.5 rounded-lg hover:bg-[#FBF9F6]">
                    <div className="flex items-start gap-2">
                      <WarningCircle size={16} weight="fill" className={`mt-0.5 ${urgencyClasses[item.urgency] || 'text-[#1E3F2D]'}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1C1B1A]">{item.title}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-[#7A766E]">
                          {item.date && <span>{item.date}</span>}
                          {item.amount > 0 && <span>{formatAmount(item.amount)}</span>}
                          <span className="capitalize">{item.urgency} priority</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/alerts')} className="rounded-lg">
                <Bell size={16} weight="fill" />
                Open Alerts Page
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex items-center gap-3 rounded-2xl px-1 py-1 hover:bg-[#FBF9F6] transition-colors">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-[#1E3F2D] text-white text-sm font-bold">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:block text-left min-w-0">
                  <p className="text-sm font-semibold text-[#1C1B1A] truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-[#7A766E] truncate">{user?.email || ''}</p>
                </div>
                <CaretDown size={14} className="text-[#7A766E]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl border-[#E8E6E1]">
              <DropdownMenuLabel className="text-[#1C1B1A]">Profile</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/my-account')} className="rounded-lg">
                <UserCircle size={16} weight="fill" />
                My Account
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/change-password')} className="rounded-lg">
                <Password size={16} weight="fill" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="rounded-lg text-[#A63C24] focus:text-[#A63C24]">
                <SignOut size={16} weight="fill" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
